/* eslint no-console: ["error", { allow: ["warn", "error"] }] */

import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import filter from 'lodash/filter';
import some from 'lodash/some';
import ApiService from '@services/apiService';

const CHECK_ROWLOCK = 'p9/rowLock/CHECK_ROWLOCK';
const CHECK_ROWLOCK_FAIL = 'p9/rowLock/CHECK_ROWLOCK_FAIL';
const CHECK_ROWLOCK_SUCCESS = 'p9/rowLock/CHECK_ROWLOCK_SUCCESS';
const CREATE_ROWLOCK = 'p9/rowLock/CREATE_ROWLOCK';
const CREATE_ROWLOCK_FAIL = 'p9/rowLock/CREATE_ROWLOCK_FAIL';
const CREATE_ROWLOCK_SUCCESS = 'p9/rowLock/CREATE_ROWLOCK_SUCCESS';

const PING_ROWLOCK = 'p9/rowLock/PING_ROWLOCK';
const PING_ROWLOCK_FAIL = 'p9/rowLock/PING_ROWLOCK_FAIL';
const PING_ROWLOCK_SUCCESS = 'p9/rowLock/PING_ROWLOCK_SUCCESS';

const REMOVE_ROWLOCK = 'p9/rowLock/REMOVE_ROWLOCK';
const REMOVE_ROWLOCK_FAIL = 'p9/rowLock/REMOVE_ROWLOCK_FAIL';
const REMOVE_ROWLOCK_SUCCESS = 'p9/rowLock/REMOVE_ROWLOCK_SUCCESS';
const ROWLOCK_CLAREITY = 'p9/rowLock/ROWLOCK_CLAREITY';

const SET_ROWLOCK_CURRENT_USER = 'p9/rowLock/SET_ROWLOCK_CURRENT_USER';
const SET_ROWLOCK_ENTITY = 'p9/rowLock/SET_ROWLOCK_ENTITY';
const SET_ROWLOCK_CALLBACK = 'p9/rowLock/SET_ROWLOCK_CALLBACK';

const TAKEOVER_ROWLOCK = 'p9/rowLock/TAKEOVER_ROWLOCK';
const TAKEOVER_ROWLOCK_FAIL = 'p9/rowLock/TAKEOVER_ROWLOCK_FAIL';
const TAKEOVER_ROWLOCK_SUCCESS = 'p9/rowLock/TAKEOVER_ROWLOCK_SUCCESS';

const initialState = {
  currentUser: {},
  createdLocks: [],
  requestType: null,
  entity: {},
  lastUpdatedOn: null,
  lastCheckedOn: null,
  requiresCheck: false,
  locked: false,
  lockedByMe: false,
  lockedByUserId: null,
  lockedByUserName: null,
  impersonatedByUserId: null,
  impersonatedByUserName: null,
  message: null,
  error: false,
  loading: false,
  clareityUrl: null,
  callback: null,
};

export default (state = initialState, action) => {
  switch (action.type) {
    case SET_ROWLOCK_CURRENT_USER:
      return {
        ...state,
        currentUser: action.payload.currentUser,
        requiresCheck: !isEqual(action.payload.currentUser, state.currentUser) && state.locked,
      };
    case SET_ROWLOCK_ENTITY:
      return {
        ...(isEqual(state.entity, action.payload.entity) ? state : initialState),
        currentUser: state.currentUser, // preserve current user
        createdLocks: state.createdLocks, // preserve created locks
        entity: isEqual(state.entity, action.payload.entity) ? state.entity : action.payload.entity,
      };
    case SET_ROWLOCK_CALLBACK:
      return {
        ...state,
        callback: action.payload.callback,
      };
    case CHECK_ROWLOCK:
    case CREATE_ROWLOCK:
    case PING_ROWLOCK:
    case REMOVE_ROWLOCK:
    case TAKEOVER_ROWLOCK:
      return {
        ...(isEqual(state.entity, action.payload.entity) ? state : initialState),
        currentUser: state.currentUser, // preserve current user
        createdLocks: state.createdLocks, // preserve created locks
        requestType: action.payload.requestType,
        entity: action.payload.entity,
        message: null,
        error: false,
        loading: true,
        clareityUrl: null,
      };
    case CHECK_ROWLOCK_FAIL:
    case CREATE_ROWLOCK_FAIL:
    case PING_ROWLOCK_FAIL:
    case REMOVE_ROWLOCK_FAIL:
    case TAKEOVER_ROWLOCK_FAIL:
      return {
        ...state,
        message: `Error encountered on ${action.type} action: ${action.error?.message} (${action.error?.response?.error?.message})`,
        error: true,
        loading: false,
      };
    case CHECK_ROWLOCK_SUCCESS: {
      const lockedByMe = action.payload.rowIsLocked && action.payload.rowIsLockedByUser === state.currentUser?.userId;
      let { createdLocks } = state;
      if (!lockedByMe && some(createdLocks, lockedEntity => isEqual(lockedEntity, state.entity))) {
        // Remove entity from created locks - no longer owner by me.
        createdLocks = filter(createdLocks, lockedEntity => !isEqual(lockedEntity, state.entity));
      }
      return {
        ...state,
        createdLocks,
        lastUpdatedOn: action.payload.lastUpdatedOn,
        lastCheckedOn: Date.now(),
        locked: action.payload.rowIsLocked,
        lockedByMe,
        lockedByUserId: action.payload.rowIsLockedByUser,
        lockedByUserName: action.payload.userName,
        impersonatedByUserId: action.payload.impersonatedByUser,
        impersonatedByUserName: action.payload.impersonatedByUserName,
        requiresCheck: false,
        loading: false,
      };
    }
    case CREATE_ROWLOCK_SUCCESS: {
      let { createdLocks } = state;
      if (action.payload.type === 'SUCCESS' && action.payload.message === 'Row Lock created' && !some(createdLocks, lockedEntity => isEqual(lockedEntity, state.entity))) {
        // Add entity to created locks.
        createdLocks = [...createdLocks, state.entity];
      }
      return {
        ...state,
        createdLocks,
        locked: action.payload.type === 'SUCCESS' || !isNil(action.payload.rowIsLockedByUser) ? true : state.locked,
        lockedByMe: action.payload.type === 'SUCCESS' ? true : state.lockedByMe,
        lockedByUserId: action.payload.type === 'SUCCESS' ? state.currentUser?.userId : action.payload.rowIsLockedByUser,
        lockedByUserName: action.payload.type === 'SUCCESS' ? state.currentUser?.userName : action.payload.userName,
        requiresCheck: isNil(action.payload.type) || (action.payload.type === 'SUCCESS' && action.payload.message === 'Row Lock updated successfully' && !some(createdLocks, lockedEntity => isEqual(lockedEntity, state.entity))),
        message: action.payload.message,
        loading: false,
      };
    }
    case PING_ROWLOCK_SUCCESS:
      return {
        ...state,
        locked: action.payload.type === 'SUCCESS' ? true : state.locked,
        lockedByMe: action.payload.type === 'SUCCESS' ? true : state.lockedByMe,
        lockedByUserId: action.payload.type === 'SUCCESS' ? state.currentUser?.userId : state.lockedByUserId,
        lockedByUserName: action.payload.type === 'SUCCESS' ? state.currentUser?.userName : state.lockedByUserName,
        requiresCheck: action.payload.type === 'FAILURE',
        message: action.payload.message,
        loading: false,
      };
    case REMOVE_ROWLOCK_SUCCESS: {
      let { createdLocks } = state;
      if (action.payload.type === 'SUCCESS' && some(createdLocks, lockedEntity => isEqual(lockedEntity, state.entity))) {
        // Remove entity from created locks.
        createdLocks = filter(createdLocks, lockedEntity => !isEqual(lockedEntity, state.entity));
      }
      return {
        ...state,
        createdLocks,
        locked: action.payload.type === 'SUCCESS' ? false : state.locked,
        lockedByMe: action.payload.type === 'SUCCESS' ? false : state.lockedByMe,
        lockedByUserId: action.payload.type === 'SUCCESS' ? null : state.lockedByUserId,
        lockedByUserName: action.payload.type === 'SUCCESS' ? null : state.lockedByUserName,
        requiresCheck: action.payload.type === 'FAILURE',
        message: action.payload.message,
        loading: false,
      };
    }
    case TAKEOVER_ROWLOCK_SUCCESS: {
      let { createdLocks } = state;
      if (action.payload.type === 'SUCCESS' && !some(createdLocks, lockedEntity => isEqual(lockedEntity, state.entity))) {
        // Add entity to created locks.
        createdLocks = [...createdLocks, state.entity];
      }
      return {
        ...state,
        createdLocks,
        locked: action.payload.type === 'SUCCESS' ? true : state.locked,
        lockedByMe: action.payload.type === 'SUCCESS' ? true : state.lockedByMe,
        lockedByUserId: action.payload.type === 'SUCCESS' ? state.currentUser?.userId : state.lockedByUserId,
        lockedByUserName: action.payload.type === 'SUCCESS' ? state.currentUser?.userName : state.lockedByUserName,
        requiresCheck: action.payload.type === 'FAILURE',
        message: action.payload.message,
        loading: false,
      };
    }
    case ROWLOCK_CLAREITY:
      return {
        ...state,
        clareityUrl: action.payload.clareityUrl,
      };
    default:
      return state;
  }
};

const checkRowLock = entity => async dispatch => {
  dispatch({
    type: CHECK_ROWLOCK,
    payload: { requestType: 'check', entity },
  });
  try {
    const result = await ApiService.RowLock.CheckRowLock(entity);
    if (result.ClareityUrl) {
      dispatch({ type: ROWLOCK_CLAREITY, payload: { clareityUrl: result.ClareityUrl } });
      return null;
    }
    return dispatch({
      type: CHECK_ROWLOCK_SUCCESS,
      payload: result,
    });
  } catch (err) {
    console.error('Error: RowLock checkRowLock', err);
    return dispatch({ type: CHECK_ROWLOCK_FAIL, error: err });
  }
};

const createRowLock = entity => async dispatch => {
  dispatch({
    type: CREATE_ROWLOCK,
    payload: { requestType: 'create', entity },
  });
  try {
    const result = await ApiService.RowLock.CreateRowLock(entity);
    if (result.ClareityUrl) {
      await dispatch({ type: ROWLOCK_CLAREITY, payload: { clareityUrl: result.ClareityUrl } });
      return false;
    }
    await dispatch({
      type: CREATE_ROWLOCK_SUCCESS,
      payload: result,
    });
    return true;
  } catch (err) {
    console.error('Error: RowLock createRowLock', err);
    await dispatch({ type: CREATE_ROWLOCK_FAIL, error: err });
    return false;
  }
};

const pingRowLock = entity => async dispatch => {
  dispatch({
    type: PING_ROWLOCK,
    payload: { requestType: 'ping', entity },
  });
  try {
    const result = await ApiService.RowLock.PingRowLock(entity);
    if (result.ClareityUrl) {
      dispatch({ type: ROWLOCK_CLAREITY, payload: { clareityUrl: result.ClareityUrl } });
      return null;
    }
    return dispatch({
      type: PING_ROWLOCK_SUCCESS,
      payload: result,
    });
  } catch (err) {
    console.error('Error: RowLock pingRowLock', err);
    return dispatch({ type: PING_ROWLOCK_FAIL, error: err });
  }
};

const removeRowLock = entity => async dispatch => {
  dispatch({
    type: REMOVE_ROWLOCK,
    payload: { requestType: 'remove', entity },
  });
  try {
    const result = await ApiService.RowLock.RemoveRowLock(entity);
    if (result.ClareityUrl) {
      dispatch({ type: ROWLOCK_CLAREITY, payload: { clareityUrl: result.ClareityUrl } });
      return null;
    }
    return dispatch({
      type: REMOVE_ROWLOCK_SUCCESS,
      payload: result,
    });
  } catch (err) {
    console.error('Error: RowLock removeRowLock', err);
    return dispatch({ type: REMOVE_ROWLOCK_FAIL, error: err });
  }
};

const setRowLockCurrentUser = currentUser => async dispatch => dispatch({
  type: SET_ROWLOCK_CURRENT_USER,
  payload: { currentUser },
});

const setRowLockEntity = entity => async dispatch => dispatch({
  type: SET_ROWLOCK_ENTITY,
  payload: { entity },
});

const setRowLockCallback = callback => async dispatch => dispatch({
  type: SET_ROWLOCK_CALLBACK,
  payload: { callback },
});

const takeoverRowLock = entity => async dispatch => {
  dispatch({
    type: TAKEOVER_ROWLOCK,
    payload: { requestType: 'takeover', entity },
  });
  try {
    const result = await ApiService.RowLock.TakeoverRowLock(entity);
    if (result.ClareityUrl) {
      dispatch({ type: ROWLOCK_CLAREITY, payload: { clareityUrl: result.ClareityUrl } });
      return null;
    }
    return dispatch({
      type: TAKEOVER_ROWLOCK_SUCCESS,
      payload: result,
    });
  } catch (err) {
    console.error('Error: RowLock takeoverRowLock', err);
    return dispatch({ type: TAKEOVER_ROWLOCK_FAIL, error: err });
  }
};

export {
  checkRowLock,
  createRowLock,
  pingRowLock,
  removeRowLock,
  setRowLockCurrentUser,
  setRowLockEntity,
  setRowLockCallback,
  takeoverRowLock,
};
